/**
 * Apply `SeoMeta` to the live document head. Idempotent: every managed tag carries a
 * `data-mb-seo` marker so re-applying (e.g. on client-side navigation) replaces rather
 * than duplicates. No-ops when there is no `document` (server rendering / non-DOM env).
 */
import type { Locale, SeoMeta } from "@mb-group/shared-types";

/** Runtime context {@link applySeo} needs beyond the CMS-provided {@link SeoMeta}. */
export interface ApplySeoOptions {
	/** Active UI/content locale; written to `<html lang>` and `og:locale`. */
	locale: Locale;
	/** Absolute URL of the current page; used as canonical fallback + og:url. */
	url: string;
}

const MARKER = "data-mb-seo";

function upsertMeta(
	head: HTMLHeadElement,
	selectorAttr: "name" | "property",
	key: string,
	content: string,
): void {
	const existing = head.querySelector<HTMLMetaElement>(`meta[${selectorAttr}="${key}"][${MARKER}]`);
	const el = existing ?? head.ownerDocument.createElement("meta");
	el.setAttribute(selectorAttr, key);
	el.setAttribute("content", content);
	el.setAttribute(MARKER, "");
	if (!existing) head.appendChild(el);
}

function upsertLink(head: HTMLHeadElement, rel: string, href: string, hreflang?: string): void {
	const selector = hreflang
		? `link[rel="${rel}"][hreflang="${hreflang}"][${MARKER}]`
		: `link[rel="${rel}"][${MARKER}]`;
	const existing = head.querySelector<HTMLLinkElement>(selector);
	const el = existing ?? head.ownerDocument.createElement("link");
	el.setAttribute("rel", rel);
	el.setAttribute("href", href);
	if (hreflang) el.setAttribute("hreflang", hreflang);
	el.setAttribute(MARKER, "");
	if (!existing) head.appendChild(el);
}

/**
 * Reconcile the document `<head>` with `seo`, writing title, description, robots,
 * canonical, Open Graph, hreflang alternates and JSON-LD structured data.
 *
 * Safe to call repeatedly (e.g. on client-side navigation): previously-managed tags
 * are reused or removed, never duplicated. No-ops in a non-DOM environment.
 *
 * @param seo Metadata for the current page (typically from the CMS).
 * @param options Page locale and URL used for `lang`, canonical and og:url.
 */
export function applySeo(seo: SeoMeta, options: ApplySeoOptions): void {
	if (typeof document === "undefined" || !document.head) return;
	const head = document.head;
	const canonical = seo.canonicalUrl ?? options.url;

	document.title = seo.title;
	document.documentElement.setAttribute("lang", options.locale);

	// Remove previously-managed hreflang/JSON-LD so stale entries never linger.
	head
		.querySelectorAll(
			`link[rel="alternate"][${MARKER}], script[type="application/ld+json"][${MARKER}]`,
		)
		.forEach((node) => {
			node.remove();
		});

	upsertMeta(head, "name", "description", seo.description);
	upsertMeta(head, "name", "robots", seo.noindex ? "noindex, nofollow" : "index, follow");
	upsertLink(head, "canonical", canonical);

	// Open Graph.
	upsertMeta(head, "property", "og:title", seo.title);
	upsertMeta(head, "property", "og:description", seo.description);
	upsertMeta(head, "property", "og:url", canonical);
	upsertMeta(head, "property", "og:locale", options.locale);
	if (seo.ogImage) upsertMeta(head, "property", "og:image", seo.ogImage.url);

	// hreflang alternates.
	for (const alt of seo.hreflang) {
		upsertLink(head, "alternate", alt.url, alt.locale);
	}

	// JSON-LD structured data.
	if (seo.structuredData) {
		const script = head.ownerDocument.createElement("script");
		script.setAttribute("type", "application/ld+json");
		script.setAttribute(MARKER, "");
		script.textContent = JSON.stringify(seo.structuredData);
		head.appendChild(script);
	}
}
